import { useParams, useLocation, Link, Navigate } from 'react-router-dom'
import { useCallback, useEffect, useRef } from 'react'
import { ChevronRight, MapPin, Building2, Award, ArrowLeft } from 'lucide-react'

import SEO from '@/components/common/SEO'
import { BreadcrumbJsonLd, FAQPageJsonLd } from '@/components/common/JsonLd'
import { useFacilities, useRegionStats, useRegions, useDongs } from '@/features/facility/api'
import FacilityCard from '@/components/facility/FacilityCard'
import { useToggleBookmark, useBookmarkIds } from '@/features/facility/api'
import { useSmartBack } from '@/lib/hooks/useSmartBack'
import { useSsrStatus } from '@/hooks/useSsrStatus'
import { correctMisattributedSido, isOfficialSido, regionHeadingLabel, toOfficialSido } from '@/lib/regions'
import NotFoundPage from '@/pages/NotFoundPage'
import type { FacilityType } from '@/types/common'
import { FACILITY_TYPE_URL_SLUGS } from '@/types/common'

// 시설유형 URL 슬러그 ↔ DB Enum 매핑
// 단일 소스(FACILITY_TYPE_URL_SLUGS, types/common.ts)에서 역방향 파생 — 슬러그 불일치 방지
const TYPE_SLUG_TO_ENUM: Record<string, FacilityType> = Object.fromEntries(
  Object.entries(FACILITY_TYPE_URL_SLUGS).map(([enumKey, slug]) => [slug, enumKey as FacilityType]),
)

const SUPPORTED_TYPE_SLUGS = Object.keys(TYPE_SLUG_TO_ENUM)

export default function RegionalFacilityPage() {
  const params = useParams<{
    id?: string      // /facilities/:id 경로에서 (FacilityOrRegionResolver 경유)
    sido?: string    // /facilities/:sido/:sigungu 경로에서
    sigungu?: string
    type?: string
  }>()
  // Resolver에서 올 때는 :id 파라미터에 지역명이 담김
  const sido = params.sido || params.id
  const sigungu = params.sigungu
  // 3번째 세그먼트(:type)는 시설유형 슬러그이거나 동(洞)이다. 슬러그 사전으로 판별.
  // 영구지침: docs/plan/2026-06-08-seo-expansion.md §3.2
  const thirdSegment = params.type
  const isTypeSlug = thirdSegment ? !!TYPE_SLUG_TO_ENUM[thirdSegment] : false
  const type = isTypeSlug ? thirdSegment : undefined
  const dong = (!isTypeSlug && thirdSegment) ? thirdSegment : undefined
  const smartBack = useSmartBack()

  // 유효하지 않은 유형이면 무시
  const facilityTypeEnum = type ? TYPE_SLUG_TO_ENUM[type] : undefined
  const facilityTypeLabel = type || undefined

  // 통계 조회
  const { data: stats } = useRegionStats(
    sido || '',
    sigungu,
    facilityTypeEnum,
    dong,
  )

  // 지역 목록 (관련 링크용)
  const { data: regions } = useRegions()

  // 동(洞) 목록 — 시군구 페이지(유형/동 미지정)에서만 하위 동 링크 노출
  const { data: dongs } = useDongs(
    sigungu && !type && !dong ? sido : undefined,
    sigungu && !type && !dong ? sigungu : undefined,
  )

  // 시설 목록 (무한 스크롤)
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFacilities({
    region: sido,
    sigungu,
    dong,
    type: facilityTypeEnum ? [facilityTypeEnum] : undefined,
    keyword: undefined,
    sortBy: 'RATING',
  })

  // 북마크
  const { data: bookmarkIds } = useBookmarkIds()
  const { toggle: toggleBookmark } = useToggleBookmark()

  // 무한 스크롤
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0]
      if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: '200px',
    })
    const el = loadMoreRef.current
    if (el) observer.observe(el)
    return () => {
      if (el) observer.unobserve(el)
    }
  }, [handleObserver])

  // SSR 응답 상태 전파 (서버 렌더 중에만 non-null)
  const ssrStatus = useSsrStatus()

  // 쿼리스트링 — 301 정규화 시 utm 등 유입 파라미터 보존용.
  // SSR 은 StaticRouter 에 쿼리 포함 전체 URL 이 들어오므로(entry-server.tsx) 양쪽 모두 채워진다.
  const { search } = useLocation()

  // ── 시도 세그먼트 정규화 (soft-404 방지) ────────────────────────────────
  // 정식 17개 시도가 아니면: 축약형(경기·전남 등)은 정식형 URL 로 301, 매핑 불가는 404.
  // 배경: HIRA 임포트 요양병원이 축약형 시도로 중복 지역 URL 을 양산 (lib/regions.ts 참조)
  // ※ 이 early return 들은 모든 훅 호출 이후에 위치해야 한다 (Rules of Hooks).
  if (sido && !isOfficialSido(sido)) {
    const official = toOfficialSido(sido)
    if (official) {
      // 쿼리스트링 유지 — SSR 301 Location 과 CSR <Navigate> 모두 이 경로를 그대로 사용
      const redirectPath = `/facilities/${official}${sigungu ? `/${sigungu}` : ''}${
        thirdSegment ? `/${thirdSegment}` : ''
      }${search}`
      if (ssrStatus) {
        // 서버: 301 + Location 으로 응답하므로 HTML 은 버려진다 —
        // StaticRouter 에서 no-op 인 <Navigate> 경고를 피해 null 렌더.
        ssrStatus.status = 301
        ssrStatus.redirect = redirectPath
        return null
      }
      return <Navigate to={redirectPath} replace />
    }
    return (
      <NotFoundPage
        title="지역을 찾을 수 없습니다"
        description="요청하신 지역 페이지가 존재하지 않습니다. 지역명을 확인해주세요."
      />
    )
  }

  // ── 잘못 귀속된 시·도 301 (광주광역시/순천시 → 전라남도/순천시) ──────────────
  // 데이터 보정 후 옛 URL 은 0곳이 되므로 404 대신 올바른 전남 URL 로 넘겨 색인·링크 신호를 잇는다.
  // 영구지침: docs/develop/seo/regional-page-spec.md §2.3
  const correctedSido = correctMisattributedSido(sido, sigungu)
  if (correctedSido) {
    const redirectPath = `/facilities/${correctedSido}/${sigungu}${
      thirdSegment ? `/${thirdSegment}` : ''
    }${search}`
    if (ssrStatus) {
      ssrStatus.status = 301
      ssrStatus.redirect = redirectPath
      return null
    }
    return <Navigate to={redirectPath} replace />
  }

  // 시설 목록 평탄화
  const facilities = data?.pages.flatMap((page) => page.data?.content ?? []) ?? []
  // stats 는 동까지 반영(useRegionStats에 dong 전달) → SSR 프리페치로 정확한 카운트
  const totalCount = stats?.totalCount ?? facilities.length

  // 페이지 제목·H1·FAQ 소제목의 지역 라벨 — 동은 `{시군구} {동}`, 여러 시도에 있는 시군구 이름은 시도 약칭까지.
  // 동/시군구 이름만 쓰면 지역 페이지 694개가 서로 같은 제목이었다 (영구지침: regional-page-spec.md §4.4.1)
  const displayType = facilityTypeLabel || '요양시설'
  const leafRegion = regionHeadingLabel({ sido, sigungu, dong })
  const displayRegion = dong
    ? `${sido} ${sigungu} ${dong}`
    : sigungu
      ? `${sido} ${sigungu}`
      : sido || ''
  const pageTitle = `${leafRegion} ${displayType} ${totalCount}곳`

  // SEO
  const seoTitle = `${leafRegion} ${displayType} ${totalCount}곳 | 평가등급·비용 비교`
  const seoDescription = `${displayRegion} ${displayType} ${totalCount}곳을 평가등급, 비용, 후기로 비교하세요.${
    stats?.gradeACount && !dong ? ` A등급 ${stats.gradeACount}곳.` : ''
  } 케어밸런스에서 무료 상담 가능합니다.`
  const seoUrl = dong
    ? `/facilities/${sido}/${sigungu}/${dong}`
    : type
      ? `/facilities/${sido}/${sigungu}/${type}`
      : sigungu
        ? `/facilities/${sido}/${sigungu}`
        : `/facilities/${sido}`

  // 빈(0곳) 지역 페이지는 색인 제외(noindex, follow).
  // 세번째 세그먼트가 유효 유형 슬러그가 아니면 동(洞)으로 간주되는데, 실존하지 않는
  // 동/서비스유형 라벨(예: 재가복지센터·방문목욕)이 들어오면 0곳 thin 페이지가 된다.
  // stats가 프리페치(SSR)되어 0으로 확정된 경우에만 noindex — 로딩 중 오탐 방지.
  const isEmptyRegion = stats?.totalCount === 0

  // 빈(0곳) 지역은 noindex 에 더해 SSR 응답 자체를 404 로 전파 (기존 EmptyRegion UI 가 404 본문).
  // stats 미확보(SSR 프리페치 실패)면 200 유지 — 오폭 금지.
  if (ssrStatus && isEmptyRegion) {
    ssrStatus.status = 404
  }

  // Breadcrumb 구성 (type/dong 중 하나만 set)
  const breadcrumbItems = [
    { name: '홈', url: '/' },
    ...(sido ? [{ name: sido, url: `/facilities/${sido}` }] : []),
    ...(sigungu ? [{ name: sigungu, url: `/facilities/${sido}/${sigungu}` }] : []),
    ...(type ? [{ name: type }] : []),
    ...(dong ? [{ name: dong }] : []),
  ]

  // 관련 지역 링크 구성
  const currentRegion = regions?.find((r) => r.sido === sido)
  const relatedSigungus = currentRegion?.sigungus.filter((s) => s !== sigungu).slice(0, 8) ?? []

  return (
    <div className="min-h-screen bg-cb-gray-50">
      <SEO title={seoTitle} description={seoDescription} url={seoUrl} noindex={isEmptyRegion} />
      <BreadcrumbJsonLd items={breadcrumbItems} />

      {/* 상단 히어로 */}
      <div className="bg-gradient-to-br from-cb-primary to-cb-primary-dark text-white">
        <div className="mx-auto max-w-4xl px-4 py-8 sm:py-12">
          {/* 뒤로가기 */}
          <button
            onClick={smartBack}
            className="mb-4 flex items-center gap-1 text-sm text-white/80 hover:text-white"
          >
            <ArrowLeft size={16} />
            뒤로가기
          </button>

          {/* Breadcrumb UI */}
          <nav className="mb-3 flex items-center gap-1 text-sm text-white/70">
            {breadcrumbItems.map((item, idx) => (
              <span key={idx} className="flex items-center gap-1">
                {idx > 0 && <ChevronRight size={14} />}
                {item.url ? (
                  <Link to={item.url} className="hover:text-white hover:underline">
                    {item.name}
                  </Link>
                ) : (
                  <span className="text-white">{item.name}</span>
                )}
              </span>
            ))}
          </nav>

          {/* H1 */}
          <h1 className="text-2xl font-bold sm:text-3xl">{pageTitle}</h1>
          <p className="mt-2 text-sm text-white/80">
            {displayRegion}의 {displayType}을 평가등급, 비용, 후기로 비교하세요
          </p>

          {/* 통계 카드 */}
          {stats && (
            <div className="mt-6 grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-white/15 px-4 py-3 text-center backdrop-blur-sm">
                <div className="flex items-center justify-center gap-1.5 text-white/70">
                  <Building2 size={14} />
                  <span className="text-xs">총 시설</span>
                </div>
                <p className="mt-1 text-xl font-bold">{stats.totalCount}곳</p>
              </div>
              <div className="rounded-xl bg-white/15 px-4 py-3 text-center backdrop-blur-sm">
                <div className="flex items-center justify-center gap-1.5 text-white/70">
                  <Award size={14} />
                  <span className="text-xs">A등급</span>
                </div>
                <p className="mt-1 text-xl font-bold">{stats.gradeACount}곳</p>
              </div>
              <div className="rounded-xl bg-white/15 px-4 py-3 text-center backdrop-blur-sm">
                <div className="flex items-center justify-center gap-1.5 text-white/70">
                  <MapPin size={14} />
                  <span className="text-xs">평균등급</span>
                </div>
                <p className="mt-1 text-xl font-bold">{stats.averageGrade}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 시설유형 필터 탭 (시군구 레벨에서만, 유형 미선택 시) */}
      {sigungu && !type && (
        <div className="sticky top-0 z-10 border-b border-cb-gray-200 bg-white">
          <div className="mx-auto flex max-w-4xl gap-2 overflow-x-auto px-4 py-3">
            <Link
              to={`/facilities/${sido}/${sigungu}`}
              className="flex-shrink-0 rounded-full bg-cb-primary px-4 py-2 text-sm font-medium text-white"
            >
              전체
            </Link>
            {SUPPORTED_TYPE_SLUGS.map((slug) => (
              <Link
                key={slug}
                to={`/facilities/${sido}/${sigungu}/${slug}`}
                className="flex-shrink-0 rounded-full bg-cb-gray-100 px-4 py-2 text-sm font-medium text-cb-gray-700 hover:bg-cb-gray-200"
              >
                {slug}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* 시설유형 필터 탭 (유형 선택된 상태) */}
      {sigungu && type && (
        <div className="sticky top-0 z-10 border-b border-cb-gray-200 bg-white">
          <div className="mx-auto flex max-w-4xl gap-2 overflow-x-auto px-4 py-3">
            <Link
              to={`/facilities/${sido}/${sigungu}`}
              className="flex-shrink-0 rounded-full bg-cb-gray-100 px-4 py-2 text-sm font-medium text-cb-gray-700 hover:bg-cb-gray-200"
            >
              전체
            </Link>
            {SUPPORTED_TYPE_SLUGS.map((slug) => (
              <Link
                key={slug}
                to={`/facilities/${sido}/${sigungu}/${slug}`}
                className={`flex-shrink-0 rounded-full px-4 py-2 text-sm font-medium ${
                  slug === type
                    ? 'bg-cb-primary text-white'
                    : 'bg-cb-gray-100 text-cb-gray-700 hover:bg-cb-gray-200'
                }`}
              >
                {slug}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* 시설 목록 */}
      <div className="mx-auto max-w-4xl px-4 py-6">
        <h2 className="mb-4 text-lg font-bold text-cb-gray-900">
          {displayType} 목록
          <span className="ml-2 text-sm font-normal text-cb-gray-500">
            총 {totalCount}곳
          </span>
        </h2>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-cb-primary border-t-transparent" />
          </div>
        ) : facilities.length === 0 ? (
          <div className="rounded-2xl bg-white p-12 text-center shadow-sm">
            <p className="text-cb-gray-500">해당 지역에 등록된 시설이 없습니다</p>
          </div>
        ) : (
          <div className="space-y-3">
            {facilities.map((facility) => (
              <FacilityCard
                key={facility.id}
                facility={facility}
                isBookmarked={bookmarkIds?.has(facility.id)}
                onToggleBookmark={(id, isBookmarked) =>
                  toggleBookmark(id, isBookmarked)
                }
              />
            ))}
          </div>
        )}

        {/* 무한 스크롤 트리거 */}
        <div ref={loadMoreRef} className="flex justify-center py-4">
          {isFetchingNextPage && (
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-cb-primary border-t-transparent" />
          )}
        </div>
      </div>

      {/* 관련 지역 링크 */}
      {/* 하위 동(洞) 링크 — 시군구 랜딩에서 동 랜딩으로 (SEO 내부링크/롱테일) */}
      {dongs && dongs.length > 0 && (
        <div className="border-t border-cb-gray-200 bg-white">
          <div className="mx-auto max-w-4xl px-4 py-8">
            <h3 className="mb-4 text-base font-bold text-cb-gray-900">
              {sigungu} 동네별 요양시설
            </h3>
            <div className="flex flex-wrap gap-2">
              {dongs.map((d) => (
                <Link
                  key={d.dong}
                  to={`/facilities/${sido}/${sigungu}/${d.dong}`}
                  className="rounded-full border border-cb-gray-200 px-4 py-2 text-sm text-cb-gray-700 hover:border-cb-primary hover:text-cb-primary"
                >
                  {d.dong} <span className="text-cb-gray-400">{d.count}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {relatedSigungus.length > 0 && (
        <div className="border-t border-cb-gray-200 bg-white">
          <div className="mx-auto max-w-4xl px-4 py-8">
            <h3 className="mb-4 text-base font-bold text-cb-gray-900">
              {sido} 다른 지역
            </h3>
            <div className="flex flex-wrap gap-2">
              {relatedSigungus.map((sg) => (
                <Link
                  key={sg}
                  to={`/facilities/${sido}/${sg}${type ? `/${type}` : ''}`}
                  className="rounded-full border border-cb-gray-200 px-4 py-2 text-sm text-cb-gray-700 hover:border-cb-primary hover:text-cb-primary"
                >
                  {sg} {displayType}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* FAQ 구조화 데이터 + 섹션 — 통계는 시군구 기준이라 동 페이지에선 생략 */}
      {stats && stats.totalCount > 0 && !dong && (
        <RegionFaq
          headingRegion={leafRegion}
          displayRegion={displayRegion}
          displayType={displayType}
          gradeACount={stats.gradeACount}
          gradeBCount={stats.gradeBCount}
          gradeCCount={stats.gradeCCount}
        />
      )}
    </div>
  )
}

/**
 * 지역 페이지 FAQ.
 *
 * 답변 문구를 **한 배열에서** 만들어 화면과 FAQPage 구조화 데이터 양쪽에 쓴다.
 * 예전에는 같은 문장을 JSON-LD 용과 화면 용으로 두 번 적어 둬서, 한쪽만 고치면
 * 구조화 데이터와 화면이 어긋날 수 있었다.
 *
 * `answer` 는 구조화 데이터용 평문, `body` 는 화면용 JSX(내부 링크 포함)다.
 * 검색엔진은 둘이 같은 내용이기를 기대하므로 **문구를 다르게 쓰지 말 것** —
 * body 는 answer 에 링크만 입힌 형태여야 한다.
 *
 * 링크를 넣은 이유: 이 지면은 GA4 7일치에서 체류 10분·전환 38%로 사이트 최고
 * 성과 지면인데, 정작 전환율이 가장 높은 비용 계산기(55.6%)로 가는 링크가
 * 하나도 없었다. FAQ 본문이 "비용 시뮬레이터에서 확인하실 수 있습니다"라고
 * 평문으로 안내만 하고 있었다. (2026-08-27, docs/plan/2026-08-27-regional-page-research-brief.md)
 *
 * 주의: details 는 기본 접힘이라 사람에게 보이는 효과는 제한적이다.
 * 접혀 있어도 DOM 에는 남으므로 크롤러는 읽는다.
 */
function RegionFaq({
  headingRegion,
  displayRegion,
  displayType,
  gradeACount,
  gradeBCount,
  gradeCCount,
}: {
  headingRegion: string
  displayRegion: string
  displayType: string
  gradeACount: number
  gradeBCount: number
  gradeCCount: number
}) {
  const linkClass = 'font-medium text-cb-primary underline underline-offset-2'

  const faqs = [
    {
      question: `${headingRegion} ${displayType} 비용은 얼마인가요?`,
      answer:
        `${displayRegion}의 ${displayType} 비용은 장기요양 등급에 따라 달라집니다. ` +
        `요양비용 계산기에서 등급별 본인부담금과 비급여 포함 예상 비용을 확인하실 수 있습니다. ` +
        `아직 등급을 받지 않으셨다면 요양등급 계산기로 예상 등급부터 확인해 보세요.`,
      body: (
        <>
          {displayRegion}의 {displayType} 비용은 장기요양 등급에 따라 달라집니다.{' '}
          <Link to="/cost-simulator" className={linkClass}>요양비용 계산기</Link>
          에서 등급별 본인부담금과 비급여 포함 예상 비용을 확인하실 수 있습니다. 아직 등급을
          받지 않으셨다면{' '}
          <Link to="/care-calculator" className={linkClass}>요양등급 계산기</Link>
          로 예상 등급부터 확인해 보세요.
        </>
      ),
    },
    {
      question: `${headingRegion}에서 평가등급 A인 ${displayType}은 몇 곳인가요?`,
      answer:
        `현재 ${displayRegion}에는 평가등급 A를 받은 ${displayType}이 ${gradeACount}곳 있습니다. ` +
        `B등급 ${gradeBCount}곳, C등급 ${gradeCCount}곳입니다.`,
      body: (
        <>
          현재 {displayRegion}에는 평가등급 A를 받은 {displayType}이 {gradeACount}곳 있습니다.
          B등급 {gradeBCount}곳, C등급 {gradeCCount}곳입니다.
        </>
      ),
    },
    {
      question: `${displayType} 입소 절차는 어떻게 되나요?`,
      answer:
        `장기요양등급을 받은 후 시설에 입소 상담을 신청하실 수 있습니다. ` +
        `등급 신청 방법은 장기요양등급 가이드에서 확인하실 수 있고, ` +
        `시설을 둘러보실 때는 시설 체크리스트로 빠뜨리는 항목 없이 확인해 보세요.`,
      body: (
        <>
          장기요양등급을 받은 후 시설에 입소 상담을 신청하실 수 있습니다. 등급 신청 방법은{' '}
          <Link to="/guide/care-grade" className={linkClass}>장기요양등급 가이드</Link>
          에서 확인하실 수 있고, 시설을 둘러보실 때는{' '}
          <Link to="/checklist" className={linkClass}>시설 체크리스트</Link>
          로 빠뜨리는 항목 없이 확인해 보세요.
        </>
      ),
    },
  ]

  return (
    <>
      <FAQPageJsonLd faqs={faqs.map(({ question, answer }) => ({ question, answer }))} />
      <div className="border-t border-cb-gray-200 bg-cb-gray-50">
        <div className="mx-auto max-w-4xl px-4 py-8">
          <h3 className="mb-4 text-base font-bold text-cb-gray-900">자주 묻는 질문</h3>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <details key={faq.question} className="rounded-xl bg-white p-4 shadow-sm">
                <summary className="cursor-pointer font-medium text-cb-gray-900">
                  {faq.question}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-cb-gray-600">{faq.body}</p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
